import { generateId } from "@/utils/id";
import { getAllDiaryDays } from './diaryDays';
import { getDbForCurrentUser } from '@/services/rxdb/database';
import {
  GOALS_HISTORY_COLLECTION,
  WEIGHT_HISTORY_COLLECTION,
  resolveMealExercise,
  type GoalHistoryDocType,
  type WeightHistoryDocType,
  type GoalBmrSettings,
  type Goal,
  type WeightEntry,
} from '@macro-tracker/models';
import { formatDateInput } from '@/utils/dateTime';
import { computeEffectiveTotals, resolveDailyGoals } from '@/utils/diaryDayTotals';

// Re-export shared canonical types so callers can import from here.
export type { GoalBmrSettings, Goal, WeightEntry };

export type GoalType = 'lose' | 'maintain' | 'gain' | 'fixed';
export type GoalStrategyType = 'percent' | 'grams_per_kg' | 'flex';
export type FlexMacro = 'carbs' | 'fat';

export const DEFAULT_GOAL_BMR_SETTINGS: GoalBmrSettings = {
  heightCm: 170,
  age: 30,
  gender: 'male',
  activityLevel: 'moderately-active',
  formula: 'revised',
  highMuscleContent: false,
  useManual: false,
  manualValue: null,
};

export type CreateGoalRequest = Omit<Goal, 'id' | 'createdAtUtc' | 'updatedAtUtc'>;
export type CreateWeightEntryRequest = Omit<WeightEntry, 'id' | 'createdAtUtc' | 'updatedAtUtc'>;

// ---------------------------------------------------------------------------
// GoalSet — simplified goal view used by diary and goals store
// ---------------------------------------------------------------------------

export type MacroTargetMode = 'grams' | 'ratio';

export type MacroTargets =
  | { mode: 'grams'; protein: number; carbs: number; fat: number }
  | { mode: 'ratio'; protein: number; carbs: number; fat: number };

export type GoalSet = {
  id: string;
  calories: number;
  macros: MacroTargets;
  effectiveDate: string;
  createdAt: string;
};

const goalToGoalSet = (goal: Goal): GoalSet => {
  let macros: MacroTargets;

  switch (goal.strategyType) {
    case 'percent':
      macros = { mode: 'ratio', protein: goal.proteinPct ?? 0, carbs: goal.carbsPct ?? 0, fat: goal.fatPct ?? 0 };
      break;
    case 'grams_per_kg':
      if (goal.proteinGPerKg == null && goal.carbsGPerKg == null && goal.fatGPerKg == null) {
        macros = { mode: 'grams', protein: goal.fixedProteinG ?? 0, carbs: goal.fixedCarbsG ?? 0, fat: goal.fixedFatG ?? 0 };
        break;
      }
      macros = { mode: 'grams', protein: goal.proteinGPerKg ?? 0, carbs: goal.carbsGPerKg ?? 0, fat: goal.fatGPerKg ?? 0 };
      break;
    case 'flex':
    default:
      macros = { mode: 'grams', protein: goal.fixedProteinG ?? 0, carbs: goal.fixedCarbsG ?? 0, fat: goal.fixedFatG ?? 0 };
      break;
  }

  return { id: goal.id, calories: goal.caloriesTarget, macros, effectiveDate: goal.effectiveFromDate, createdAt: goal.createdAtUtc };
};

/** Find the GoalSet effective on or before the given date (YYYY-MM-DD). */
export const findActiveGoalSet = (goalSets: GoalSet[], date: string): GoalSet | null => {
  const target = toTimestamp(date);
  if (Number.isNaN(target)) return null;
  for (let i = goalSets.length - 1; i >= 0; i -= 1) {
    const effective = toTimestamp(goalSets[i].effectiveDate);
    if (!Number.isNaN(effective) && effective <= target) return goalSets[i];
  }
  return null;
};

// ---------------------------------------------------------------------------
// RxDB ↔ Goal / WeightEntry mapping
// ---------------------------------------------------------------------------

const docToGoal = (doc: GoalHistoryDocType): Goal => ({
  id: doc.id,
  effectiveFromDate: doc.effectiveFromDate,
  goalType: doc.goalType,
  caloriesTarget: doc.caloriesTarget,
  strategyType: doc.strategyType,
  proteinPct: doc.proteinPct ?? undefined,
  carbsPct: doc.carbsPct ?? undefined,
  fatPct: doc.fatPct ?? undefined,
  proteinGPerKg: doc.proteinGPerKg ?? undefined,
  carbsGPerKg: doc.carbsGPerKg ?? undefined,
  fatGPerKg: doc.fatGPerKg ?? undefined,
  flexMacro: doc.flexMacro ?? undefined,
  fixedProteinG: doc.fixedProteinG ?? undefined,
  fixedCarbsG: doc.fixedCarbsG ?? undefined,
  fixedFatG: doc.fixedFatG ?? undefined,
  bmrSettings: doc.bmrSettings,
  workoutFuelingTargets: doc.workoutFuelingTargets ?? undefined,
  createdAtUtc: doc.createdAtUtc,
  updatedAtUtc: doc.updatedAtUtc,
});

const goalToDoc = (goal: Goal, userId: string): Omit<GoalHistoryDocType, '_deleted'> => ({
  id: goal.id,
  userId,
  effectiveFromDate: goal.effectiveFromDate,
  goalType: goal.goalType,
  caloriesTarget: goal.caloriesTarget,
  strategyType: goal.strategyType,
  proteinPct: goal.proteinPct ?? null,
  carbsPct: goal.carbsPct ?? null,
  fatPct: goal.fatPct ?? null,
  proteinGPerKg: goal.proteinGPerKg ?? null,
  carbsGPerKg: goal.carbsGPerKg ?? null,
  fatGPerKg: goal.fatGPerKg ?? null,
  flexMacro: (goal.flexMacro ?? null) as 'carbs' | 'fat' | null,
  fixedProteinG: goal.fixedProteinG ?? null,
  fixedCarbsG: goal.fixedCarbsG ?? null,
  fixedFatG: goal.fixedFatG ?? null,
  bmrSettings: goal.bmrSettings,
  workoutFuelingTargets: goal.workoutFuelingTargets ?? null,
  createdAtUtc: goal.createdAtUtc,
  updatedAtUtc: goal.updatedAtUtc,
  deletedAt: null,
});

const docToWeightEntry = (doc: WeightHistoryDocType): WeightEntry => ({
  id: doc.id,
  weightKg: doc.weightKg,
  measuredAtUtc: doc.measuredAtUtc,
  localDate: doc.localDate,
  source: doc.source ?? undefined,
  notes: doc.notes ?? undefined,
  bodyFatPct: doc.bodyFatPct ?? undefined,
  measurements: doc.measurements ?? undefined,
  createdAtUtc: doc.createdAtUtc,
  updatedAtUtc: doc.updatedAtUtc,
});

const isActiveWeightDoc = (doc: WeightHistoryDocType): boolean => !doc.deletedAt;

const weightEntryToDoc = (entry: WeightEntry, userId: string): Omit<WeightHistoryDocType, '_deleted'> => ({
  id: entry.id,
  userId,
  weightKg: entry.weightKg,
  measuredAtUtc: entry.measuredAtUtc,
  localDate: entry.localDate,
  source: entry.source ?? null,
  notes: entry.notes ?? null,
  bodyFatPct: entry.bodyFatPct ?? null,
  measurements: entry.measurements ?? null,
  createdAtUtc: entry.createdAtUtc,
  updatedAtUtc: entry.updatedAtUtc,
  deletedAt: null,
});

const getCurrentUserId = async (): Promise<string> => {
  const { useAuthSessionStore } = await import('@/stores/authSessionStore');
  return useAuthSessionStore().user?.id ?? '';
};

const parseDateOnly = (value: string): Date | null => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim());
  if (!match) {
    return null;
  }

  const year = Number(match[1]);
  const month = Number(match[2]) - 1;
  const day = Number(match[3]);
  return new Date(year, month, day);
};

const toTimestamp = (value: string): number => {
  const dateOnly = parseDateOnly(value);
  if (dateOnly) {
    return dateOnly.getTime();
  }
  return new Date(value).getTime();
};

// Total order used for goal resolution. Primary key is the effective date, but
// ties MUST break deterministically: while duplicate rows for the same date
// transiently coexist (legacy data, or the window before a dedupe/tombstone
// syncs), resolvers that scan from the end (findActiveGoal / findActiveGoalSet /
// getGoalForDay via a stable re-sort) would otherwise pick an arbitrary row and
// surface a different calorie/macro value on each reopen. Ordering the
// most-recently-updated (then -created, then highest id) row LAST makes those
// end-scans always pick the same row — the same one createGoal reuses and
// dedupe keeps — so the displayed goal is stable even before cleanup runs.
const compareForResolution = (a: Goal, b: Goal): number => {
  const byDate = toTimestamp(a.effectiveFromDate) - toTimestamp(b.effectiveFromDate);
  if (byDate !== 0) return byDate;
  const byUpdated = (a.updatedAtUtc ?? '').localeCompare(b.updatedAtUtc ?? '');
  if (byUpdated !== 0) return byUpdated;
  const byCreated = (a.createdAtUtc ?? '').localeCompare(b.createdAtUtc ?? '');
  if (byCreated !== 0) return byCreated;
  return a.id.localeCompare(b.id);
};

const sortByEffectiveDate = (goals: Goal[]): Goal[] =>
  [...goals].sort(compareForResolution);

const sortByLocalDate = (weights: WeightEntry[]): WeightEntry[] =>
  [...weights].sort((a, b) => toTimestamp(a.localDate) - toTimestamp(b.localDate));

const getDateRange = (fromDate: string, toDate: string): string[] => {
  const dates: string[] = [];
  const currentDate = parseDateOnly(fromDate);
  const endDate = parseDateOnly(toDate);
  if (!currentDate || !endDate) {
    return dates;
  }

  while (currentDate <= endDate) {
    dates.push(formatDateInput(currentDate));
    currentDate.setDate(currentDate.getDate() + 1);
  }
  return dates;
};

const calculateTrend = (data: number[], alpha = 0.3): number[] => {
  if (data.length === 0) return [];
  const result: number[] = [data[0]];
  for (let i = 1; i < data.length; i += 1) {
    result.push(alpha * data[i] + (1 - alpha) * result[i - 1]);
  }
  return result;
};

const getWeightForDate = (weights: WeightEntry[], date: string, fallback = 70): number => {
  const target = toTimestamp(date);
  for (let i = weights.length - 1; i >= 0; i -= 1) {
    const effective = toTimestamp(weights[i].localDate);
    if (!Number.isNaN(effective) && effective <= target) {
      return weights[i].weightKg;
    }
  }
  return fallback;
};

const findActiveGoal = (goals: Goal[], date: string): Goal | null => {
  const target = toTimestamp(date);
  if (Number.isNaN(target)) return null;
  for (let i = goals.length - 1; i >= 0; i -= 1) {
    const effective = toTimestamp(goals[i].effectiveFromDate);
    if (!Number.isNaN(effective) && effective <= target) {
      return goals[i];
    }
  }
  return null;
};

export const goalsService = {
  async getGoalsHistory(): Promise<Goal[]> {
    const db = await getDbForCurrentUser();
    const docs = await db[GOALS_HISTORY_COLLECTION]
      .find({ selector: { deletedAt: null } }).exec();
    return sortByEffectiveDate(docs.map((d) => docToGoal(d.toMutableJSON())));
  },

  async getGoalById(id: string): Promise<Goal | null> {
    const db = await getDbForCurrentUser();
    const doc = await db[GOALS_HISTORY_COLLECTION].findOne(id).exec();
    return doc ? docToGoal(doc.toMutableJSON()) : null;
  },

  async getActiveGoal(date?: string): Promise<Goal | null> {
    const db = await getDbForCurrentUser();
    const docs = await db[GOALS_HISTORY_COLLECTION]
      .find({ selector: { deletedAt: null } }).exec();
    const goals = sortByEffectiveDate(docs.map((d) => docToGoal(d.toMutableJSON())));
    const targetDate = date ?? formatDateInput();
    return findActiveGoal(goals, targetDate);
  },

  async createGoal(goal: CreateGoalRequest): Promise<Goal> {
    const db = await getDbForCurrentUser();
    const userId = await getCurrentUserId();
    const now = new Date().toISOString();

    // Upsert BY EFFECTIVE DATE, not by a fresh random id. RxDB's `upsert` keys on
    // the primary key `id`; minting a new id on every save always INSERTs a new
    // doc, which is what produced duplicate goals_history rows for the same date.
    // Instead, reuse the id of any existing (non-deleted) goal already effective
    // on this date so the save updates in place. Reusing the id also keeps diary
    // `goalSnapshot.sourceGoalId` references stable per date.
    const existingDocs = await db[GOALS_HISTORY_COLLECTION]
      .find({ selector: { effectiveFromDate: goal.effectiveFromDate, deletedAt: null } })
      .exec();
    const existing = existingDocs
      .map((d) => d.toMutableJSON())
      .sort((a, b) => (b.updatedAtUtc ?? '').localeCompare(a.updatedAtUtc ?? ''));

    const reuse = existing[0];
    const id = reuse?.id ?? generateId();
    const createdAtUtc = reuse?.createdAtUtc ?? now;
    const record: Goal = { id, ...goal, createdAtUtc, updatedAtUtc: now };
    await db[GOALS_HISTORY_COLLECTION].upsert(goalToDoc(record, userId));

    // Self-heal any pre-existing duplicates for this date: keep the reused row
    // and soft-delete the rest so exactly one active row remains for the date.
    for (const dup of existing.slice(1)) {
      if (dup.id !== id) {
        await goalsService.deleteGoal(dup.id);
      }
    }

    return record;
  },

  /**
   * Collapse duplicate goals that share an `effectiveFromDate` down to a single
   * active row per date (per current user). Keeps the most-recently-updated
   * (then most-recently-created) goal and soft-deletes the rest via the standard
   * delete path so the deletions replicate. Idempotent — a second run finds
   * nothing to remove. Returns the number of goals soft-deleted.
   */
  /**
   * Collapse legacy duplicate goals that share an effectiveFromDate down to the
   * most-recent one per date. Returns the effective dates that were actually
   * collapsed (a duplicate removed), so callers can refresh anything that
   * depended on the previously-ambiguous resolution (e.g. re-run the planner for
   * those days). Idempotent: once each date holds a single goal it returns [].
   */
  async dedupeGoalsByEffectiveDate(): Promise<string[]> {
    const db = await getDbForCurrentUser();
    const docs = await db[GOALS_HISTORY_COLLECTION]
      .find({ selector: { deletedAt: null } }).exec();

    const byDate = new Map<string, GoalHistoryDocType[]>();
    for (const doc of docs) {
      const data = doc.toMutableJSON();
      const list = byDate.get(data.effectiveFromDate) ?? [];
      list.push(data);
      byDate.set(data.effectiveFromDate, list);
    }

    const affectedDates: string[] = [];
    for (const [effectiveFromDate, list] of byDate.entries()) {
      if (list.length <= 1) continue;
      // Most-recent first: prefer updatedAtUtc, fall back to createdAtUtc.
      list.sort((a, b) => {
        const byUpdated = (b.updatedAtUtc ?? '').localeCompare(a.updatedAtUtc ?? '');
        if (byUpdated !== 0) return byUpdated;
        return (b.createdAtUtc ?? '').localeCompare(a.createdAtUtc ?? '');
      });
      let removedForDate = 0;
      for (const dup of list.slice(1)) {
        if (await goalsService.deleteGoal(dup.id)) removedForDate += 1;
      }
      if (removedForDate > 0) affectedDates.push(effectiveFromDate);
    }
    return affectedDates;
  },

  async updateGoal(id: string, updates: Partial<CreateGoalRequest>): Promise<Goal | null> {
    const db = await getDbForCurrentUser();
    const doc = await db[GOALS_HISTORY_COLLECTION].findOne(id).exec();
    if (!doc) return null;
    const now = new Date().toISOString();
    await doc.incrementalModify((d) => {
      Object.assign(d, updates);
      d.updatedAtUtc = now;
      return d;
    });
    const updated = await db[GOALS_HISTORY_COLLECTION].findOne(id).exec();
    return updated ? docToGoal(updated.toMutableJSON()) : null;
  },

  async deleteGoal(id: string): Promise<boolean> {
    const db = await getDbForCurrentUser();
    const doc = await db[GOALS_HISTORY_COLLECTION].findOne(id).exec();
    if (!doc) return false;
    const now = new Date().toISOString();
    await doc.incrementalModify((d) => { d.deletedAt = now; d.updatedAtUtc = now; return d; });
    return true;
  },

  async getGoalsByDateRange(startDate: string, endDate: string): Promise<Goal[]> {
    const db = await getDbForCurrentUser();
    const docs = await db[GOALS_HISTORY_COLLECTION]
      .find({ selector: { deletedAt: null } }).exec();
    const goals = sortByEffectiveDate(docs.map((d) => docToGoal(d.toMutableJSON())));
    const start = toTimestamp(startDate);
    const end = toTimestamp(endDate);
    const [min, max] = start <= end ? [start, end] : [end, start];
    return goals.filter((goal) => {
      const effective = toTimestamp(goal.effectiveFromDate);
      return !Number.isNaN(effective) && effective >= min && effective <= max;
    });
  },

  async getWeightHistory(from?: string, to?: string): Promise<WeightEntry[]> {
    const db = await getDbForCurrentUser();
    const docs = await db[WEIGHT_HISTORY_COLLECTION]
      .find()
      .sort('measuredAtUtc')
      .exec();
    const sorted = sortByLocalDate(
      docs
        .map((d) => d.toMutableJSON())
        .filter(isActiveWeightDoc)
        .map(docToWeightEntry),
    );
    if (!from && !to) return sorted;
    const start = from ? toTimestamp(from) : -Infinity;
    const end = to ? toTimestamp(to) : Infinity;
    return sorted.filter((entry) => {
      const date = toTimestamp(entry.localDate);
      return date >= start && date <= end;
    });
  },

  async getLatestWeight(): Promise<WeightEntry | null> {
    const db = await getDbForCurrentUser();
    const docs = await db[WEIGHT_HISTORY_COLLECTION]
      .find().exec();
    const sorted = sortByLocalDate(
      docs
        .map((d) => d.toMutableJSON())
        .filter(isActiveWeightDoc)
        .map(docToWeightEntry),
    );
    return sorted.at(-1) ?? null;
  },

  async createWeightEntry(entry: CreateWeightEntryRequest): Promise<WeightEntry> {
    const db = await getDbForCurrentUser();
    const userId = await getCurrentUserId();
    const now = new Date().toISOString();
    const record: WeightEntry = { id: generateId(), ...entry, createdAtUtc: now, updatedAtUtc: now };
    await db[WEIGHT_HISTORY_COLLECTION].upsert(weightEntryToDoc(record, userId));
    return record;
  },

  async updateWeightEntry(id: string, updates: Partial<CreateWeightEntryRequest>): Promise<WeightEntry | null> {
    const db = await getDbForCurrentUser();
    const doc = await db[WEIGHT_HISTORY_COLLECTION].findOne(id).exec();
    if (!doc) return null;
    const now = new Date().toISOString();
    await doc.incrementalModify((d) => {
      Object.assign(d, updates);
      d.updatedAtUtc = now;
      return d;
    });
    const updated = await db[WEIGHT_HISTORY_COLLECTION].findOne(id).exec();
    return updated ? docToWeightEntry(updated.toMutableJSON()) : null;
  },

  async deleteWeightEntry(id: string): Promise<boolean> {
    const db = await getDbForCurrentUser();
    const doc = await db[WEIGHT_HISTORY_COLLECTION].findOne(id).exec();
    if (!doc) return false;
    const now = new Date().toISOString();
    await doc.incrementalModify((d) => { d.deletedAt = now; d.updatedAtUtc = now; return d; });
    return true;
  },

  async getChartData(from?: string, to?: string): Promise<{
    weight: { date: string; weight: number; trend: number; bodyFatPct: number | null }[];
    calories: {
      date: string;
      calories: number;
      protein: number;
      carbs: number;
      fat: number;
      exerciseCaloriesBurned: number;
      goalCalories: number;
      goalProtein: number;
      goalCarbs: number;
      goalFat: number;
    }[];
    macros: { date: string; protein: number; carbs: number; fat: number }[];
  }> {
    const fromDate = from ?? '1970-01-01';
    const toDate = to ?? formatDateInput();
    const db = await getDbForCurrentUser();
    const [goalDocs, weightDocs, days] = await Promise.all([
      db[GOALS_HISTORY_COLLECTION].find({ selector: { deletedAt: null } }).exec(),
      db[WEIGHT_HISTORY_COLLECTION].find().exec(),
      getAllDiaryDays(),
    ]);
    const goals = goalDocs.map((d) => docToGoal(d.toMutableJSON()));
    const weights = weightDocs
      .map((d) => d.toMutableJSON())
      .filter(isActiveWeightDoc)
      .map(docToWeightEntry);

    const sortedGoals = sortByEffectiveDate(goals);
    const sortedWeights = sortByLocalDate(weights);
    const dates = getDateRange(fromDate, toDate);
    const dayByDate = new Map(days.map((day) => [day.date, day]));

    const weightSeries = sortedWeights
      .filter((entry) => {
        const date = toTimestamp(entry.localDate);
        return date >= toTimestamp(fromDate) && date <= toTimestamp(toDate);
      })
      .map((entry) => ({ date: entry.localDate, weight: entry.weightKg, bodyFatPct: entry.bodyFatPct ?? null }));

    const trend = calculateTrend(weightSeries.map((entry) => entry.weight));
    const weight = weightSeries.map((entry, index) => ({
      date: entry.date,
      weight: entry.weight,
      trend: trend[index] ?? entry.weight,
      bodyFatPct: entry.bodyFatPct,
    }));

    const calories = dates.map((date) => {
      const day = dayByDate.get(date);
      const totals = day
        ? computeEffectiveTotals(day.body)
        : { calories: 0, protein: 0, carbs: 0, fat: 0 };

      // Exercise energy is counted once, from each workout meal's burn
      // (workoutActuals, falling back to the planned/custom exercise). A completed
      // workout ALSO owns a mirror `exercise` entry whose calories equal
      // `workoutActuals.caloriesBurned` (see diaryStore.completeWorkoutPlan), so
      // summing exercise entries on top would double-count the burn — which in
      // turn understated energy availability on training days.
      const exerciseCaloriesBurned = day
        ? (day.body.meals ?? [])
            .filter((plan) => !plan.deletedAt)
            .reduce((sum, plan) => {
              const burned = plan.workoutActuals?.caloriesBurned ?? resolveMealExercise(plan)?.caloriesBurned ?? 0;
              return sum + (burned > 0 ? burned : 0);
            }, 0)
        : 0;

      const dayGoals = day ? resolveDailyGoals(day.body) : null;
      const goal = dayGoals ? null : findActiveGoal(sortedGoals, date);
      const weightForGoal = getWeightForDate(sortedWeights, date);
      const goalMacros = goal ? goalsService.calculateDailyMacroTargets(goal, weightForGoal) : null;
      const goalCalories = dayGoals?.calories ?? goal?.caloriesTarget ?? 0;
      const goalProtein = dayGoals?.protein ?? goalMacros?.protein ?? 0;
      const goalCarbs = dayGoals?.carbs ?? goalMacros?.carbs ?? 0;
      const goalFat = dayGoals?.fat ?? goalMacros?.fat ?? 0;

      return {
        date,
        calories: totals.calories,
        protein: totals.protein,
        carbs: totals.carbs,
        fat: totals.fat,
        exerciseCaloriesBurned,
        goalCalories,
        goalProtein,
        goalCarbs,
        goalFat,
      };
    });

    const macros = calories.map((day) => ({
      date: day.date,
      protein: day.protein,
      carbs: day.carbs,
      fat: day.fat,
    }));

    return { weight, calories, macros };
  },

  async getGoalSets(): Promise<GoalSet[]> {
    const goals = await goalsService.getGoalsHistory();
    return goals.map(goalToGoalSet);
  },

  async getGoalSetById(id: string): Promise<GoalSet | null> {
    const goal = await goalsService.getGoalById(id);
    return goal ? goalToGoalSet(goal) : null;
  },

  async getGoalSetsByDateRange(startDate: string, endDate: string): Promise<GoalSet[]> {
    const goals = await goalsService.getGoalsByDateRange(startDate, endDate);
    return goals.map(goalToGoalSet);
  },

  calculateDailyMacroTargets(goal: Goal, weightKg: number): { protein: number; carbs: number; fat: number } {
    if (goal.strategyType === 'percent') {
      return {
        protein: (goal.proteinPct ?? 0) / 100 * (goal.caloriesTarget / 4),
        carbs: (goal.carbsPct ?? 0) / 100 * (goal.caloriesTarget / 4),
        fat: (goal.fatPct ?? 0) / 100 * (goal.caloriesTarget / 9),
      };
    }
    if (goal.strategyType === 'grams_per_kg') {
      const hasScaledTargets = goal.proteinGPerKg != null
        || goal.carbsGPerKg != null
        || goal.fatGPerKg != null;

      if (!hasScaledTargets) {
        return {
          protein: goal.fixedProteinG ?? 0,
          carbs: goal.fixedCarbsG ?? 0,
          fat: goal.fixedFatG ?? 0,
        };
      }

      const proteinG = (goal.proteinGPerKg ?? 0) * weightKg;
      const carbsG = (goal.carbsGPerKg ?? 0) * weightKg;
      const fatG = (goal.fatGPerKg ?? 0) * weightKg;

      if (!goal.flexMacro) {
        return {
          protein: proteinG,
          carbs: carbsG,
          fat: fatG,
        };
      }

      const proteinKcal = proteinG * 4;
      const fatKcal = fatG * 9;
      const remainingKcal = goal.caloriesTarget - (proteinKcal + fatKcal);

      if (goal.flexMacro === 'carbs') {
        return {
          protein: proteinG,
          carbs: Math.max(0, remainingKcal / 4),
          fat: fatG,
        };
      }

      const carbsKcal = carbsG * 4;
      const remainingAfterCarbs = goal.caloriesTarget - (proteinKcal + carbsKcal);
      return {
        protein: proteinG,
        carbs: carbsG,
        fat: fatG + Math.max(0, remainingAfterCarbs / 9),
      };
    }
    const proteinBase = goal.fixedProteinG ?? 0;
    const carbsBase = goal.fixedCarbsG ?? 0;
    const fatBase = goal.fixedFatG ?? 0;
    const baseCalories = (proteinBase * 4) + (carbsBase * 4) + (fatBase * 9);
    const remainingCalories = Math.max(0, goal.caloriesTarget - baseCalories);

    if (goal.flexMacro === 'carbs') {
      return {
        protein: proteinBase,
        carbs: carbsBase + (remainingCalories / 4),
        fat: fatBase,
      };
    }

    return {
      protein: proteinBase,
      carbs: carbsBase,
      fat: fatBase + (remainingCalories / 9),
    };
  },
};
